import { forwardRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import navStyle from "../styles/nav.module.scss";
import { vendorIcon, designerIcon, guestIcon } from "./Icons";
import UserMenuOptions from "../utilities/UserMenuOptions";
import {
  formatBrandLabel,
  getAccountEmail,
  getDisplayUserType,
  hasPersonalAccountEmail,
  identifierLooksLikeEmail,
  isSharedBrandSession,
} from "../utilities/loginAccount";

const normalizePath = (path) => {
  if (!path || path === "./") {
    return "/";
  }

  const cleanPath = path.replace(/^\.\//, "/");
  return cleanPath.length > 1 ? cleanPath.replace(/\/$/, "") : cleanPath;
};

const isCollectionPath = (path) => normalizePath(path) === "/collection";

const UserBox = forwardRef(function UserBox(
  {
    userInformation,
    userMenuIsVisible,
    setUserMenuIsVisible,
    changelogOutUser,
    collectionCount = 0,
    includeCollectionLink = false,
  },
  ref,
) {
  const location = useLocation();

  const sharedBrandSession = isSharedBrandSession(userInformation);
  const displayUserType = getDisplayUserType(userInformation);
  const brandLabel = formatBrandLabel(userInformation.vendor) || "brand";

  const menu = useMemo(() => {
    const currentPath = normalizePath(location.pathname);

    const items = (
      UserMenuOptions(userInformation.userType, { sharedBrandSession }) || []
    ).filter((item) => {
      return normalizePath(item.buttonAction) !== currentPath;
    });

    const hasCollection = items.some((item) =>
      isCollectionPath(item.buttonAction),
    );

    if (
      includeCollectionLink &&
      currentPath !== "/collection" &&
      !hasCollection
    ) {
      return [
        {
          id: "collection",
          buttonName: "Collection",
          buttonAction: "./collection",
        },
        ...items,
      ];
    }

    return items;
  }, [
    includeCollectionLink,
    location.pathname,
    sharedBrandSession,
    userInformation.userType,
  ]);

  const accountEmail = useMemo(() => {
    if (hasPersonalAccountEmail(userInformation)) {
      return userInformation.email;
    }

    return getAccountEmail(userInformation);
  }, [userInformation]);

  const accountIdentity = useMemo(() => {
    const vendor = String(userInformation.vendor || "").trim();
    let userName = String(userInformation.userName || "").trim();

    if (identifierLooksLikeEmail(userName)) {
      userName = userName.split("@")[0];
    } else if (!userName && identifierLooksLikeEmail(accountEmail)) {
      userName = accountEmail.split("@")[0];
    }

    if (isSharedBrandSession(userInformation) && vendor) {
      return formatBrandLabel(vendor);
    }

    if (userName && vendor) {
      return `${userName} from ${vendor}`;
    }

    if (userName) {
      return userName;
    }

    if (vendor) {
      return vendor;
    }

    return "";
  }, [accountEmail, userInformation]);

  const userRoleLabel = (type) => {
    const roleTitle =
      type === "vendoradmin"
        ? "Admin view"
        : type === "vendor"
          ? "Vendor view"
          : type === "designer"
            ? "Designer view"
            : type === "guest"
              ? "Guest view"
              : "";

    if (!roleTitle) {
      return null;
    }

    const roleIcon =
      type === "vendoradmin" || type === "vendor"
        ? vendorIcon
        : type === "designer"
          ? designerIcon
          : guestIcon;

    return (
      <div className={navStyle.userBoxRole}>
        <span className={navStyle.userBoxRoleIcon}>{roleIcon}</span>
        <div className={navStyle.userBoxRoleCopy}>
          <span className={navStyle.userBoxRoleTitle}>{roleTitle}</span>
          {accountIdentity ? (
            <span className={navStyle.userBoxRoleIdentity}>
              {accountIdentity}
            </span>
          ) : null}
          {accountEmail ? (
            <span className={navStyle.userBoxRoleEmail}>{accountEmail}</span>
          ) : null}
        </div>
      </div>
    );
  };

  const handleLogOutUser = () => {
    setUserMenuIsVisible(false);
    changelogOutUser();
  };

  if (!userInformation.isLoggedIn) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={`${navStyle.userBox} ${
        userMenuIsVisible ? navStyle.userBoxOpen : ""
      }`}
      role="menu"
      aria-hidden={!userMenuIsVisible}
    >
      <div className={navStyle.userBoxContent}>
        <div className={navStyle.userBoxHeader}>
          {userRoleLabel(displayUserType)}
        </div>
        {menu.length > 0 ? (
          <nav className={navStyle.userBoxNav} aria-label="Account navigation">
            {menu.map((item) => (
              <Link
                key={item.id}
                to={item.buttonAction}
                className={navStyle.menuButton}
                role="menuitem"
                onClick={() => setUserMenuIsVisible(false)}
              >
                {item.buttonName}
                {isCollectionPath(item.buttonAction) ? (
                  <span className={navStyle.menuCollectionCount}>
                    {collectionCount}
                  </span>
                ) : null}
              </Link>
            ))}
          </nav>
        ) : null}
        <div className={navStyle.userBoxFooter}>
          {sharedBrandSession ? (
            <div className={navStyle.userBoxGenericPrompt}>
              <p className={navStyle.userBoxGenericText}>
                You are logged in as a generic {brandLabel} user. Create a
                personal account to keep your own profile.
              </p>
              <Link
                to="/register"
                className={navStyle.userBoxRegister}
                role="menuitem"
                onClick={() => setUserMenuIsVisible(false)}
              >
                Create a personal account
              </Link>
            </div>
          ) : null}
          <button
            type="button"
            className={navStyle.userBoxLogout}
            onClick={handleLogOutUser}
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
});

export default UserBox;
